import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { Upload, FileText, Trash2, ShieldCheck, AlertCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CERT_TYPES = ['GST', 'ISO 9001', 'FSSAI', 'BIS', 'MSME', 'Trade License', 'Other'];
const MAX_SIZE = 5 * 1024 * 1024;

const StatusBadge = ({ status }) => {
  if (status === 'verified') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800" data-testid="cert-status-verified">
        <ShieldCheck className="w-3 h-3" /> Verified
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800" data-testid="cert-status-rejected">
        <AlertCircle className="w-3 h-3" /> Rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800" data-testid="cert-status-pending">
      <Clock className="w-3 h-3" /> Pending review
    </span>
  );
};

const FilePreview = ({ url, ext }) => {
  const abs = url?.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  if (ext === 'pdf') {
    return (
      <a href={abs} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center h-16 w-16 border border-gray-200 rounded bg-red-50 text-red-600 hover:bg-red-100" data-testid="cert-preview-pdf">
        <FileText className="w-8 h-8" />
      </a>
    );
  }
  return (
    <a href={abs} target="_blank" rel="noopener noreferrer">
      <img src={abs} alt="certificate" className="h-16 w-16 object-cover border border-gray-200 rounded" data-testid="cert-preview-img" />
    </a>
  );
};

const VendorCertificatesPanel = ({ token }) => {
  const { toast } = useToast();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [certType, setCertType] = useState('GST');
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);

  const authClient = axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authClient.get('/vendor/me/certificates');
      setCerts(r.data || []);
    } catch (e) {
      console.warn('Load certs failed', e);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!file) return toast({ title: 'Please select a file', variant: 'destructive' });
    if (file.size > MAX_SIZE) return toast({ title: 'File too large', description: 'Maximum 5 MB', variant: 'destructive' });

    const fd = new FormData();
    fd.append('file', file);
    fd.append('cert_type', certType);
    fd.append('name', name || certType);

    setUploading(true);
    try {
      await authClient.post('/vendor/me/certificates', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'Certificate uploaded', description: 'It will be visible publicly once an admin verifies it.' });
      setFile(null); setName(''); setCertType('GST');
      // reset native file input
      const el = document.getElementById('cert-file-input');
      if (el) el.value = '';
      await load();
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const onDelete = async (cert) => {
    if (!window.confirm(`Delete "${cert.name}"? This cannot be undone.`)) return;
    try {
      await authClient.delete(`/vendor/me/certificates/${cert.id}`);
      toast({ title: 'Certificate deleted' });
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6" data-testid="vendor-certificates-panel">
      <div className="bg-white border border-gray-200 rounded-md p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-1 flex items-center gap-2"><Upload className="w-5 h-5 text-[#ee5e23]" /> Upload a new certificate</h3>
        <p className="text-xs text-gray-500 mb-4">Max 5 MB · PDF / PNG / JPG. Up to 10 certificates per vendor. Verified certificates appear publicly on your profile and earn buyer trust.</p>

        <form onSubmit={onUpload} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <Label htmlFor="cert-type" className="text-xs">Type</Label>
            <select
              id="cert-type"
              value={certType}
              onChange={(e) => setCertType(e.target.value)}
              className="w-full h-10 text-sm border border-gray-300 rounded px-2 mt-1 focus:outline-none focus:border-[#ee5e23]"
              data-testid="cert-type-select"
            >
              {CERT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="cert-name" className="text-xs">
              Display name {certType === 'Other' && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id="cert-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={certType === 'Other' ? 'e.g. Pollution Clearance Certificate' : `e.g. ${certType} Certificate 2025`}
              className="mt-1"
              data-testid="cert-name-input"
            />
          </div>
          <div className="md:col-span-3">
            <Label htmlFor="cert-file-input" className="text-xs">File</Label>
            <input
              id="cert-file-input"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm border border-gray-300 rounded p-2 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#ee5e23] file:text-white file:cursor-pointer cursor-pointer"
              data-testid="cert-file-input"
            />
            {file && <div className="text-xs text-gray-500 mt-1">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button type="submit" disabled={uploading || !file} className="bg-[#ee5e23] hover:bg-[#d24e15]" data-testid="cert-upload-submit">
              <Upload className="w-4 h-4 mr-1" /> {uploading ? 'Uploading…' : 'Upload certificate'}
            </Button>
          </div>
        </form>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Your certificates ({certs.length})</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading…</div>
        ) : certs.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm" data-testid="no-certs">No certificates uploaded yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-4 py-2 text-left">Preview</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Uploaded</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {certs.map((c) => (
                <tr key={c.id} className="border-t border-gray-100" data-testid={`cert-row-${c.id}`}>
                  <td className="px-4 py-2"><FilePreview url={c.file_url} ext={c.file_ext} /></td>
                  <td className="px-4 py-2 text-gray-700">{c.cert_type}</td>
                  <td className="px-4 py-2 font-medium text-gray-900">{c.name}</td>
                  <td className="px-4 py-2"><StatusBadge status={c.status} />{c.status === 'rejected' && c.note && <div className="text-xs text-red-600 mt-1">{c.note}</div>}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{new Date(c.uploaded_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => onDelete(c)} className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Delete" data-testid={`cert-delete-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default VendorCertificatesPanel;
